import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Car, List, Settings as SettingsIcon, LogOut, MessageSquare, MessageCircle, ChevronRight, ChevronDown, ChevronUp, Calendar, Clock, Users } from 'lucide-react';
import BASE_URL from './config';
import RideChat from './RideChat';
import './index.css';
import logo from './images/logo.png';
import { getLocationDisplay } from './locations';
import useUnreadCounts from './useUnreadCounts';

/**
 * Messages page — shows all ride group chats the user is part of.
 * Each ride chat is listed as a conversation thread.
 */
function Messages() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState('');
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatRideId, setChatRideId] = useState(null);
  const [showPastChats, setShowPastChats] = useState(false);

  // Auto-open chat if ?ride=<id> is in the URL
  useEffect(() => {
    const rideParam = searchParams.get('ride');
    if (rideParam) {
      setChatRideId(parseInt(rideParam, 10));
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const unreadCounts = useUnreadCounts();

  useEffect(() => {
    fetch(`${BASE_URL}/check_login`, { method: 'GET', credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.isLoggedIn) {
          setUser(data.user);
        } else {
          navigate('/');
        }
      })
      .catch(() => navigate('/'));
  }, [navigate]);


  // Fetch user's rides once we have the username
  useEffect(() => {
    if (!user) return;
    fetchUserRides();
  }, [user]);

  const fetchUserRides = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/getRideInfo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username: user }),
      });
      if (response.ok) {
        const data = await response.json();
        setRides(data);
      }
    } catch (err) {
      console.error('Failed to fetch rides for messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    window.location.href = `${BASE_URL}/logout`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getAirportName = (code) => {
    const airports = {
      'LGA': 'LaGuardia',
      'JFK': 'JFK',
      'BDL': 'Bradley',
      'EWR': 'Newark',
      'Tweed': 'Tweed NH'
    };
    return airports[code] || code;
  };

  return (
    <div className="app-container">
      {/* Header */}
      <header className="header">
        <div className="logo-container" onClick={() => navigate('/feed')}>
          <div className="logo-wrapper logo-light-mode">
            <img src={logo} alt="Logo" className="logo" />
          </div>
          <h1>Bulldog Carpool</h1>
        </div>

        <nav className="nav-buttons">
          <button className="nav-button" onClick={() => navigate('/feed')}>
            <List className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
            Feed
          </button>
          <button className="nav-button" onClick={() => navigate('/rides')}>
            <Car className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
            My Rides{unreadCounts.ride_join > 0 && <span className="nav-badge">{unreadCounts.ride_join}</span>}
          </button>
          <button className="nav-button active" onClick={() => navigate('/messages')}>
            <MessageSquare className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
            Messages{unreadCounts.message > 0 && <span className="nav-badge">{unreadCounts.message}</span>}
          </button>
          <button className="nav-button" onClick={() => navigate('/settings')}>
            <SettingsIcon className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
            Settings
          </button>
          <button className="nav-button" onClick={handleLogout}>
            <LogOut className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
            Log Out
          </button>
        </nav>
      </header>

      <div className="rides-dashboard">
        <div className="tab-content">
          <h2>Messages</h2>
          <p style={{ color: '#6B7280', fontSize: '0.9rem', marginBottom: '20px' }}>
            Your ride group chats
          </p>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
              Loading your conversations...
            </div>
          ) : rides.length === 0 ? (
            <div className="empty-state">
              <MessageCircle className="empty-state-icon w-12 h-12" stroke="#00356B" strokeWidth={1.5} />
              <h3>No conversations yet</h3>
              <p>Join a ride to start chatting with other riders.</p>
              <button
                className="action-btn action-btn-primary"
                onClick={() => navigate('/feed')}
                style={{ marginTop: '12px' }}
              >
                Browse Rides
              </button>
            </div>
          ) : (() => {
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const isExpired = (ride) => {
              if (!ride.departureDate) return false;
              const d = new Date(ride.departureDate + 'T23:59:59');
              return d < today;
            };
            const activeChats = rides.filter(r => !isExpired(r));
            const pastChats = rides.filter(r => isExpired(r)).sort((a, b) => (b.departureDate || '').localeCompare(a.departureDate || ''));

            const renderThread = (ride, muted) => {
                const seatsInfo = `${ride.spotsFilled}/${ride.spotsAvailable}`;
                return (
                  <div
                    key={ride.requestId}
                    onClick={() => setChatRideId(ride.requestId)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '14px 16px',
                      backgroundColor: '#fff',
                      borderRadius: '10px',
                      border: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      opacity: muted ? 0.6 : 1,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#00356B';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 53, 107, 0.08)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: 0 }}>
                      {/* Chat icon */}
                      <div style={{
                        width: '42px',
                        height: '42px',
                        borderRadius: '10px',
                        backgroundColor: '#EFF6FF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <MessageCircle size={20} stroke="#00356B" strokeWidth={1.5} />
                      </div>

                      {/* Ride info */}
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{
                          fontWeight: '600',
                          color: '#1F2937',
                          fontSize: '0.95rem',
                          marginBottom: '4px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {getLocationDisplay(ride.fromLocation)} → {getLocationDisplay(ride.toLocation || ride.destination)} — {formatDate(ride.departureDate)}
                        </div>
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '10px',
                          fontSize: '0.8rem',
                          color: '#6B7280',
                        }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                            <Clock size={12} /> {formatTime(ride.startTime || ride.departureTime)}
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                            <Users size={12} /> {seatsInfo}
                          </span>
                          {ride.computedStatus === 'COMPLETED' && (
                            <span style={{ color: '#059669', fontWeight: '500' }}>Completed</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {unreadCounts.byRide[ride.requestId]?.message > 0 && (
                        <span className="notif-count" style={{ position: 'static' }}>{unreadCounts.byRide[ride.requestId].message}</span>
                      )}
                      <ChevronRight size={18} stroke="#9CA3AF" />
                    </div>
                  </div>
                );
            };

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {activeChats.map(r => renderThread(r, false))}
                {pastChats.length > 0 && (
                  <>
                    <div
                      style={{ marginTop: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', userSelect: 'none' }}
                      onClick={() => setShowPastChats(!showPastChats)}
                    >
                      <h3 style={{ color: '#6B7280', fontSize: '1rem', fontWeight: '600', margin: 0 }}>Past Conversations ({pastChats.length})</h3>
                      {showPastChats ? <ChevronUp size={18} stroke="#6B7280" /> : <ChevronDown size={18} stroke="#6B7280" />}
                    </div>
                    {showPastChats && pastChats.map(r => renderThread(r, true))}
                  </>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Ride Chat slide-in */}
      <RideChat
        rideId={chatRideId}
        currentUser={user}
        isOpen={chatRideId !== null}
        onClose={() => { setChatRideId(null); unreadCounts.refresh(); }}
      />
    </div>
  );
}

export default Messages;
